import React, {
  ForwardedRef,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {getTeamReportData, getTeamReportDataList} from './proxy.service';
import {
  ProxyH5Header,
  goTo,
  toPriceStr,
  useInfinitePager,
} from './proxy-h5.shared';

type SortType = 'none' | 'asc' | 'desc';

type TeamReportListContent = {
  userId: number;
  userCount: number;
  rechargeAmount: string;
  betAmount: string;
  commissionAmount: string;
};

type TeamReportPanelContent = {
  newUserCount: number;
  rechargeUserCount: number;
  rechargeAmount: string;
  rechargeCommissionAmount: string;
  betAmount: string;
  betCommissionAmount: string;
};

type TeamHeadTitle = {
  columnKey: string;
  text: string;
  hasSort?: boolean;
  className?: string;
};

type TeamBodyCell = {
  text: ReactNode;
  className?: string;
};

type TeamTableScrollRef = {
  scrollTo: (left: number) => void;
};

const emptyImage = require('@components/assets/imgs/empty.webp');

const panelTabs = [
  {key: '0', title: 'Today'},
  {key: '1', title: 'Yesterday'},
  {key: '3', title: '3days'},
  {key: '7', title: '7days'},
  {key: '15', title: '15d'},
  {key: '30', title: '30d'},
];

const filterOptions = [
  {value: '0', title: 'Today'},
  {value: '1', title: 'Yesterday'},
  {value: '7', title: '7 Days'},
  {value: '15', title: '15 Days'},
  {value: '30', title: '30 Days'},
];

const tierClassMap: Record<string, string> = {
  '1': 'bg-[linear-gradient(90deg,#FCC_-1.04%,#F26969_102.08%)] text-[#800A0A]',
  '2': 'bg-[linear-gradient(90deg,#CCEDFF_-1.04%,#60BDE5_102.08%)] text-[#044F70]',
  '3': 'bg-[linear-gradient(90deg,#FFF1CC_-1.04%,#EEBF45_102.08%)] text-[#775005]',
  '4': 'bg-[linear-gradient(90deg,#CCD7FF_-1.04%,#5982ED_102.08%)] text-[#052169]',
};

const formatApiDate = (date: number) => dayjs(date).format('YYYYMMDD');

const formatShortDate = (startDate?: number, endDate?: number) => {
  if (!startDate || !endDate) {
    return 'All';
  }
  if (dayjs(startDate).isSame(endDate, 'day')) {
    return dayjs(startDate).format('DD/MM');
  }
  return `${dayjs(startDate).format('DD/MM')}-${dayjs(endDate).format(
    'DD/MM',
  )}`;
};

const getCalendarDays = (monthDate: dayjs.Dayjs) => {
  const startOfMonth = monthDate.startOf('month');
  const daysInMonth = monthDate.daysInMonth();
  const startOffset = startOfMonth.day();
  const cellCount = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const firstCell = startOfMonth.subtract(startOffset, 'day');
  return Array(cellCount)
    .fill(0)
    .map((_, index) => firstCell.add(index, 'day'));
};

const getRangeByFilter = (
  value: string,
): [number | undefined, number | undefined] => {
  if (!value) {
    return [undefined, undefined];
  }
  const dayMs = 24 * 60 * 60 * 1000;
  if (value === '0') {
    return [Date.now(), Date.now()];
  }
  return [Date.now() - dayMs * +value, Date.now() - dayMs];
};

const nextSort = (sort: SortType): SortType => {
  if (sort === 'none') {
    return 'asc';
  }
  if (sort === 'asc') {
    return 'desc';
  }
  return 'none';
};

const sortToApi = (sortMap: Record<string, SortType>) =>
  Object.keys(sortMap).reduce<Record<string, number>>((result, key) => {
    const map: Record<SortType, number> = {none: 0, asc: 1, desc: 2};
    result[key] = map[sortMap[key]] || 0;
    return result;
  }, {});

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM11.333 11.333 14 14"
      stroke="#9FA5AC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DateIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <rect x="2.5" y="3.5" width="11" height="10" rx="1" stroke="#5F6975" />
    <path d="M2.5 6.5H13.5" stroke="#5F6975" />
    <path d="M5 2.5V4.5M11 2.5V4.5" stroke="#5F6975" strokeLinecap="round" />
  </svg>
);

const DownIcon = ({className = 'h-3 w-3'}: {className?: string}) => (
  <svg viewBox="0 0 12 12" fill="none" className={className}>
    <path
      d="M3 4.5 6 7.5 9 4.5"
      stroke="#9FA5AC"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" className="h-3.5 w-3.5">
    <path
      d="M2 3h10L8.2 7.3V11L5.8 12V7.3L2 3Z"
      stroke="#9FA5AC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortArrowIcon = ({sort}: {sort: SortType}) => (
  <span className="flex flex-col leading-[0.375rem]">
    <svg viewBox="0 0 8 5" className="h-1.5 w-2">
      <path
        d="M4 0 8 5H0L4 0Z"
        fill={sort === 'asc' ? 'var(--proxy-primary)' : '#9F9F9F'}
      />
    </svg>
    <svg viewBox="0 0 8 5" className="h-1.5 w-2">
      <path
        d="M4 5 0 0h8L4 5Z"
        fill={sort === 'desc' ? 'var(--proxy-primary)' : '#9F9F9F'}
      />
    </svg>
  </span>
);

const RowArrow = () => (
  <svg viewBox="0 0 12 12" fill="none" className="h-3 w-3">
    <path
      d="M7.82313 5.34417C8.21781 5.68598 8.21781 6.29825 7.82313 6.64005L4.84672 9.2177C4.2916 9.69845 3.42845 9.30412 3.42845 8.56976L3.42845 3.41447C3.42845 2.68011 4.2916 2.28578 4.84672 2.76653L7.82313 5.34417Z"
      fill="#9FA5AC"
    />
  </svg>
);

const PanelNewUserIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      d="M11.3337 14V12.6667C11.3337 11.9594 11.0527 11.2811 10.5526 10.781C10.0525 10.281 9.37424 10 8.66699 10H3.33366C2.62641 10 1.94814 10.281 1.44804 10.781C0.947944 11.2811 0.666992 11.9594 0.666992 12.6667V14"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.00065 7.33333C7.47341 7.33333 8.66732 6.13943 8.66732 4.66667C8.66732 3.19391 7.47341 2 6.00065 2C4.52789 2 3.33398 3.19391 3.33398 4.66667C3.33398 6.13943 4.52789 7.33333 6.00065 7.33333Z"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.334 14.0002V12.6669C15.3335 12.0761 15.1369 11.5021 14.7749 11.0351C14.4129 10.5682 13.9061 10.2346 13.334 10.0869"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.667 2.08691C11.2406 2.23378 11.749 2.56738 12.1121 3.03512C12.4752 3.50286 12.6722 4.07813 12.6722 4.67025C12.6722 5.26236 12.4752 5.83763 12.1121 6.30537C11.749 6.77311 11.2406 7.10671 10.667 7.25358"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PanelRechargeIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      d="M14.0003 2.66602H2.00033C1.26395 2.66602 0.666992 3.26297 0.666992 3.99935V11.9993C0.666992 12.7357 1.26395 13.3327 2.00033 13.3327H14.0003C14.7367 13.3327 15.3337 12.7357 15.3337 11.9993V3.99935C15.3337 3.26297 14.7367 2.66602 14.0003 2.66602Z"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M0.666992 6.66602H15.3337"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PanelRechargeUserIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      d="M5.71498 10L3.33366 10C2.62641 10 1.94814 10.281 1.44804 10.781C0.947944 11.2811 0.666992 11.9594 0.666992 12.6667V14"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.00065 7.33333C7.47341 7.33333 8.66732 6.13943 8.66732 4.66667C8.66732 3.19391 7.47341 2 6.00065 2C4.52789 2 3.33398 3.19391 3.33398 4.66667C3.33398 6.13943 4.52789 7.33333 6.00065 7.33333Z"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.4286 14.2859C13.3221 14.2859 14.8571 12.7508 14.8571 10.8573C14.8571 8.96373 13.3221 7.42871 11.4286 7.42871C9.53502 7.42871 8 8.96373 8 10.8573C8 12.7508 9.53502 14.2859 11.4286 14.2859Z"
      stroke="#fff"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.8101 10.0049L12.8265 9.94219H12.7617H12.2493C12.2253 9.81893 12.1855 9.70503 12.1295 9.60078H12.6191H12.6576L12.6675 9.56363L12.8139 9.01285L12.8307 8.95H12.7656H10.7266H10.6094H10.5709H10.1602H10.1205L10.1115 8.98863L9.95131 9.67417L9.93697 9.73555H10H10.7266C10.957 9.73555 11.1099 9.79138 11.1991 9.89082L11.1995 9.89127C11.2144 9.90746 11.2282 9.92443 11.2408 9.94219H10.1406H10.1019L10.0922 9.97965L9.9516 10.5226L9.9354 10.5852H10H11.2786C11.2772 10.5881 11.2757 10.591 11.2741 10.5938C11.2295 10.6784 11.1625 10.7458 11.0715 10.7961C10.9819 10.8456 10.8677 10.8719 10.7266 10.8719H10.0664H10.0166L10.0164 10.9217L10.0145 11.41L10.0144 11.4283L10.0262 11.4423L11.3621 13.0322L11.3771 13.05H11.4004H12.3203H12.3703V13V12.9648V12.947L12.359 12.9332L11.2116 11.5289C11.4473 11.4733 11.6429 11.3911 11.7965 11.281C11.961 11.1641 12.0823 11.0172 12.159 10.8405C12.1941 10.7611 12.2212 10.676 12.2405 10.5852H12.6191H12.6577L12.6675 10.5479L12.8101 10.0049Z"
      fill="#fff"
      stroke="#fff"
      strokeWidth="0.1"
    />
  </svg>
);

const PanelBettingIcon = () => (
  <svg viewBox="0 0 15 15" fill="none" className="h-4 w-4">
    <circle
      cx="7.49902"
      cy="7.49902"
      r="6.36364"
      stroke="#fff"
      strokeWidth="1.27273"
    />
    <circle
      cx="7.49982"
      cy="7.49982"
      r="3.27273"
      stroke="#fff"
      strokeWidth="1.09091"
    />
    <path
      d="M7.65039 4.04004L7.65039 1.94976"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M4.03516 7.65527L1.94488 7.65527"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M5.0957 10.21L3.61765 11.688"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M10.2061 10.21L11.6841 11.688"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M7.65039 13.3584L7.65039 11.2681"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M13.3555 7.65527L11.2652 7.65527"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M11.6836 3.62109L10.2055 5.09914"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
    <path
      d="M3.61621 3.62109L5.09426 5.09914"
      stroke="#fff"
      strokeWidth="1.09091"
      strokeLinecap="round"
    />
  </svg>
);

function SearchInputH5({
  value,
  onChange,
  onSearch,
  onClearSearch,
}: {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
}) {
  const {t} = useTranslation();

  return (
    <div className="flex w-full flex-row gap-2">
      <div className="relative h-10 flex-1 rounded border border-[#dee1e9] bg-white">
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <SearchIcon />
        </div>
        <input
          value={value}
          inputMode="tel"
          onChange={event => {
            const nextValue = event.target.value;
            if (!/^[0-9]*$/.test(nextValue)) {
              return;
            }
            onChange(nextValue);
            if (!nextValue) {
              onClearSearch();
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && value) {
              onSearch();
            }
          }}
          placeholder={t('proxy.team.search')}
          className="h-full w-full bg-transparent pl-9 pr-3 text-sm text-[var(--proxy-main)] outline-none placeholder:text-[var(--proxy-muted)]"
        />
      </div>
      <button
        type="button"
        disabled={!value}
        onClick={onSearch}
        className="h-10 rounded bg-[var(--proxy-primary)] px-4 text-sm font-bold text-white disabled:opacity-30">
        Search
      </button>
    </div>
  );
}

function PanelValue({
  icon,
  title,
  value,
  placement = 'left',
}: {
  icon?: ReactNode;
  title: string;
  value: ReactNode;
  placement?: 'left' | 'right';
}) {
  return (
    <div
      className={`flex flex-col gap-y-1 ${
        placement === 'right' ? 'items-end text-right' : 'items-start'
      }`}>
      <div
        className={`flex flex-row gap-x-1 text-xs text-white opacity-70 ${
          placement === 'right' ? 'justify-end' : ''
        }`}>
        {icon}
        <span>{title}</span>
      </div>
      <div className="text-sm font-bold text-white">{value}</div>
    </div>
  );
}

function TeamReportPanelH5() {
  const {t} = useTranslation();
  const [filterValue, setFilterValue] = useState('0');
  const [startDate, setStartDate] = useState<number>(Date.now());
  const [endDate, setEndDate] = useState<number>(Date.now());
  const [panel, setPanel] = useState<TeamReportPanelContent>();
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTeamReportData({
      startDate: formatApiDate(startDate),
      endDate: formatApiDate(endDate),
    })
      .then((result: any) => setPanel(result))
      .finally(() => setLoading(false));
  }, [endDate, startDate]);

  const handleFilterValue = (value: string) => {
    setFilterValue(value);
    const [nextStartDate, nextEndDate] = getRangeByFilter(value);
    if (nextStartDate && nextEndDate) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  };

  return (
    <div className="relative flex flex-col gap-y-4 bg-[#c70f00] px-3 py-5 text-white">
      <div className="flex flex-row gap-x-3 overflow-hidden">
        <div className="proxy-h5-scroll flex h-9 flex-1 items-end gap-x-2.5 overflow-x-auto overflow-y-hidden border-b border-white/70 px-2">
          {panelTabs.map(item => (
            <button
              type="button"
              key={item.key}
              onClick={() => handleFilterValue(item.key)}
              className={`relative h-full shrink-0 px-0 pb-2 text-xs text-white ${
                filterValue === item.key ? 'text-sm font-bold' : ''
              }`}>
              {item.title}
              {filterValue === item.key && (
                <span className="absolute bottom-0 left-1/2 h-1 w-6 -translate-x-1/2 bg-white" />
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setDatePickerOpen(true)}
          className="flex h-7 min-w-0 flex-row items-center justify-center gap-x-1 rounded border border-[#dee1e9] bg-white px-2 text-xs text-[var(--proxy-second)]">
          <DateIcon />
          <span className="min-w-10">
            {formatShortDate(startDate, endDate)}
          </span>
          <DownIcon />
        </button>
      </div>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#c70f00]/40 text-xs">
          Loading...
        </div>
      )}
      <div className="flex flex-row items-center justify-between px-3">
        <PanelValue
          icon={<PanelNewUserIcon />}
          title={t('proxy.team-report.new-user')}
          value={panel ? panel.newUserCount : '-'}
        />
        <PanelValue
          placement="right"
          icon={<PanelRechargeUserIcon />}
          title={t('proxy.team-report.betting-users')}
          value={panel ? panel.rechargeUserCount : '-'}
        />
      </div>
      <div className="flex flex-row items-stretch justify-between px-3">
        <div className="flex flex-col gap-y-2">
          <PanelValue
            icon={<PanelRechargeIcon />}
            title={t('proxy.team-report.recharge')}
            value={panel ? toPriceStr(+panel.rechargeAmount) : '-'}
          />
          <PanelValue
            title={t('proxy.team-report.recharge-commission')}
            value={panel ? toPriceStr(+panel.rechargeCommissionAmount) : '-'}
          />
        </div>
        <div className="w-px bg-white/30" />
        <div className="flex flex-col gap-y-2">
          <PanelValue
            icon={<PanelBettingIcon />}
            title={t('proxy.team-report.betting')}
            value={panel ? toPriceStr(+panel.betAmount) : '-'}
          />
          <PanelValue
            title={t('proxy.team-report.betting-commission')}
            value={panel ? toPriceStr(+panel.betCommissionAmount) : '-'}
          />
        </div>
      </div>
      <DatePickerSheet
        open={datePickerOpen}
        startValue={startDate}
        endValue={endDate}
        onClose={() => setDatePickerOpen(false)}
        onSelect={(nextStartDate, nextEndDate) => {
          setFilterValue('');
          setStartDate(nextStartDate);
          setEndDate(nextEndDate);
        }}
      />
    </div>
  );
}

const TeamTableHeadH5 = forwardRef(function TeamTableHeadH5(
  {
    className,
    topContent,
    headTitles,
    onChangeSort,
    onScroll,
  }: {
    className?: string;
    topContent?: ReactNode;
    headTitles: TeamHeadTitle[];
    onChangeSort?: (sortMap: Record<string, SortType>) => void;
    onScroll?: (scrollLeft: number) => void;
  },
  ref: ForwardedRef<TeamTableScrollRef>,
) {
  const [sortMap, setSortMap] = useState<Record<string, SortType>>({});
  const tableHeaderScrollRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    scrollTo(left: number) {
      tableHeaderScrollRef.current?.scrollTo({left});
    },
  }));

  const handleSortChange = (columnKey: string) => {
    const nextSortMap = {
      ...sortMap,
      [columnKey]: nextSort(sortMap[columnKey] || 'none'),
    };
    setSortMap(nextSortMap);
    onChangeSort?.(nextSortMap);
  };

  const handleScroll = () => {
    window.setTimeout(() => {
      onScroll?.(tableHeaderScrollRef.current?.scrollLeft || 0);
    }, 20);
  };

  return (
    <div className={`flex flex-col gap-y-3 ${className || ''}`}>
      {topContent}
      <div
        ref={tableHeaderScrollRef}
        onScroll={handleScroll}
        className="flex h-8 flex-row gap-x-2 overflow-x-auto bg-[#D8E2E7] text-xs text-[var(--proxy-second)]">
        {headTitles.map((title, index) => (
          <div
            key={title.columnKey}
            className={`${
              index === 0
                ? 'sticky left-0 border-r border-r-white bg-[#D8E2E7]'
                : ''
            } flex flex-none items-center ${title.className || ''}`}>
            {title.hasSort ? (
              <button
                type="button"
                className="flex flex-row items-center gap-x-1 border-0 bg-transparent p-0 text-xs text-[var(--proxy-second)]"
                onClick={() => handleSortChange(title.columnKey)}>
                <span>{title.text}</span>
                <SortArrowIcon sort={sortMap[title.columnKey] || 'none'} />
              </button>
            ) : (
              title.text
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

function TierTab({
  tier,
  active,
  onClick,
}: {
  tier: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="relative flex-1 border-0 bg-transparent p-0"
      onClick={onClick}>
      <span
        className={`mx-auto flex h-7 w-16 items-center justify-center rounded-[1.25rem] text-xs ${tierClassMap[tier]}`}>
        tier {tier}
      </span>
      {active && (
        <span className="absolute -bottom-2 left-1/2 h-1 w-5 -translate-x-1/2 bg-[var(--proxy-primary)]" />
      )}
    </button>
  );
}

function FilterSheet({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
}) {
  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/45"
      onClick={onClose}>
      <div
        className="w-full rounded-t-lg bg-white pt-0"
        onClick={event => event.stopPropagation()}>
        <div className="flex h-14 items-center justify-between px-5">
          <div className="text-sm font-bold text-[var(--proxy-main)]">
            Select a time
          </div>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-lg font-bold leading-none text-[var(--proxy-second)]"
            onClick={onClose}>
            ×
          </button>
        </div>
        <div className="flex flex-col px-5 pb-3">
          {filterOptions.map(item => (
            <button
              type="button"
              key={item.value}
              className="flex h-12 flex-row items-center justify-between border-0 bg-white p-3 text-left"
              onClick={() => {
                onSelect(item.value);
                onClose();
              }}>
              <div className="text-sm font-medium text-[var(--proxy-second)]">
                {item.title}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function DatePickerSheet({
  open,
  startValue,
  endValue,
  onClose,
  onSelect,
}: {
  open: boolean;
  startValue?: number;
  endValue?: number;
  onClose: () => void;
  onSelect: (startDate: number, endDate: number) => void;
}) {
  const [selectingStart, setSelectingStart] = useState(true);
  const [selectedStartDate, setSelectedStartDate] = useState(
    dayjs(startValue || Date.now()),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs(endValue || startValue || Date.now()),
  );
  const [monthDate, setMonthDate] = useState(
    dayjs(startValue || Date.now()).startOf('month'),
  );

  useEffect(() => {
    if (open) {
      const now = Date.now();
      const nextStartDate = dayjs(startValue || now);
      const nextEndDate = dayjs(endValue || startValue || now);
      setSelectingStart(true);
      setSelectedStartDate(nextStartDate);
      setSelectedEndDate(nextEndDate);
      setMonthDate(nextStartDate.startOf('month'));
    }
  }, [endValue, open, startValue]);

  if (!open) {
    return null;
  }

  const calendarDays = getCalendarDays(monthDate);
  const activeDate = selectingStart ? selectedStartDate : selectedEndDate;

  return (
    <div className="fixed inset-0 z-50 bg-black/45">
      <div className="mx-auto min-h-full w-full max-w-[var(--max-screen-width)] bg-white px-5 pt-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="text-sm font-bold text-[var(--proxy-main)]">
            Select a time
          </div>
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-xl font-bold leading-none text-[var(--proxy-second)]"
            onClick={onClose}>
            ×
          </button>
        </div>
        <div className="mb-1 text-sm font-medium text-[var(--proxy-second)]">
          {selectingStart ? 'Start:' : 'End:'}
        </div>
        <div className="overflow-hidden rounded border border-[#9FA5AC]">
          <div className="relative flex h-16 items-center justify-center bg-[#f5f5f5]">
            <button
              type="button"
              className="absolute left-4 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-2xl text-[#c9c9c9]"
              onClick={() => setMonthDate(prev => prev.subtract(1, 'month'))}>
              ‹
            </button>
            <div className="text-base font-bold text-black">
              {monthDate.format('MMMM YYYY')}
            </div>
          </div>
          <div className="grid h-10 grid-cols-7 items-center border-t border-[#c9c9c9] bg-[#f5f5f5] text-center text-sm font-bold text-black">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(item => (
              <div key={item}>{item}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 bg-white pb-3 pt-2">
            {calendarDays.map(date => {
              const isCurrentMonth = date.month() === monthDate.month();
              const isSelected = date.isSame(activeDate, 'day');
              const isBeforeStart =
                !selectingStart && date.isBefore(selectedStartDate, 'day');
              return (
                <button
                  type="button"
                  key={date.format('YYYY-MM-DD')}
                  disabled={isBeforeStart}
                  className={`mx-auto my-1 flex h-7 w-7 items-center justify-center rounded border-0 p-0 text-sm font-medium ${
                    isSelected
                      ? 'bg-[#bde9f5] text-[var(--proxy-main)]'
                      : isBeforeStart
                      ? 'bg-transparent text-[#d9dcdf]'
                      : isCurrentMonth
                      ? 'bg-transparent text-black'
                      : 'bg-transparent text-[#bfc3c7]'
                  }`}
                  onClick={() => {
                    if (selectingStart) {
                      setSelectedStartDate(date);
                      setSelectedEndDate(date);
                      setMonthDate(date.startOf('month'));
                      setSelectingStart(false);
                      return;
                    }
                    setSelectedEndDate(date);
                    onSelect(selectedStartDate.valueOf(), date.valueOf());
                    onClose();
                  }}>
                  {date.date()}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const MyTeamsH5 = forwardRef(function MyTeamsH5(
  {
    tier,
    searching,
    dateRange,
    onChangeTier,
    onChangeDateRange,
    onChangeSort,
    onScroll,
  }: {
    tier: string;
    searching: boolean;
    dateRange?: [number, number];
    onChangeTier: (tier: string) => void;
    onChangeDateRange: (
      filterValue: string,
      startDate?: number,
      endDate?: number,
    ) => void;
    onChangeSort: (sortMap: Record<string, SortType>) => void;
    onScroll: (scrollLeft: number) => void;
  },
  ref: ForwardedRef<TeamTableScrollRef>,
) {
  const {t} = useTranslation();
  const [filterOpen, setFilterOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const headTitles = useMemo<TeamHeadTitle[]>(
    () => [
      {
        columnKey: 'userId',
        text: t('proxy.team-report.user-id'),
        hasSort: false,
        className: 'w-[7.5rem] justify-center',
      },
      {
        columnKey: 'userSort',
        text: t('proxy.team-report.user'),
        hasSort: true,
        className: 'w-14 px-3',
      },
      {
        columnKey: 'rechargeSort',
        text: t('proxy.team-report.recharge'),
        hasSort: true,
        className: 'w-[6rem] justify-center',
      },
      {
        columnKey: 'bettingSort',
        text: t('proxy.team-report.betting'),
        hasSort: true,
        className: 'w-[6rem] justify-center',
      },
      {
        columnKey: 'commissionSort',
        text: t('proxy.team-report.commission'),
        hasSort: true,
        className: 'w-[6rem] justify-center',
      },
    ],
    [t],
  );

  const topContent = searching ? null : (
    <>
      <div className="flex flex-row items-center justify-between bg-white px-3 pt-3">
        <span className="text-sm font-bold text-[var(--proxy-main)]">
          {t('proxy.team-report.my-teams')}
        </span>
        <div className="flex flex-row gap-x-1">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="flex h-7 min-w-0 flex-row items-center justify-center gap-x-1 rounded border border-[#dee1e9] bg-white px-2 py-0 text-xs text-[var(--proxy-second)]">
            <FilterIcon />
            <span>Filter</span>
            <DownIcon />
          </button>
          <button
            type="button"
            onClick={() => setDatePickerOpen(true)}
            className="flex h-7 min-w-0 flex-row items-center justify-center gap-x-1 rounded border border-[#dee1e9] bg-white px-2 py-0 text-xs text-[var(--proxy-second)]">
            <DateIcon />
            <span className="min-w-10">
              {formatShortDate(dateRange?.[0], dateRange?.[1])}
            </span>
            <DownIcon />
          </button>
        </div>
      </div>
      <div className="flex h-9 min-h-9 items-start bg-white">
        {['1', '2', '3', '4'].map(item => (
          <TierTab
            key={item}
            tier={item}
            active={tier === item}
            onClick={() => onChangeTier(item)}
          />
        ))}
      </div>
    </>
  );

  return (
    <>
      <TeamTableHeadH5
        ref={ref}
        className={`sticky top-0 z-10 ${searching ? 'pt-0' : 'bg-white'}`}
        topContent={topContent}
        headTitles={headTitles}
        onChangeSort={onChangeSort}
        onScroll={onScroll}
      />
      <FilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onSelect={value => {
          const [startDate, endDate] = getRangeByFilter(value);
          onChangeDateRange(value, startDate, endDate);
        }}
      />
      <DatePickerSheet
        open={datePickerOpen}
        startValue={dateRange?.[0]}
        endValue={dateRange?.[1]}
        onClose={() => setDatePickerOpen(false)}
        onSelect={(startDate, endDate) =>
          onChangeDateRange('custom', startDate, endDate)
        }
      />
    </>
  );
});

const TeamTableBodyH5 = forwardRef(function TeamTableBodyH5(
  {
    data,
    loading,
    onScroll,
  }: {
    data: TeamReportListContent[];
    loading: boolean;
    onScroll: (scrollLeft: number) => void;
  },
  ref: ForwardedRef<TeamTableScrollRef>,
) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    scrollTo(left: number) {
      scrollRef.current?.scrollTo({left});
    },
  }));

  const bodyCell = useCallback(
    (item: TeamReportListContent): TeamBodyCell[] => [
      {
        className: 'w-[7.5rem] justify-center',
        text: (
          <button
            type="button"
            onClick={() =>
              goTo('ProxyCommissionDetailUser', {userId: item.userId})
            }
            className="flex flex-row items-center gap-x-1 border-0 bg-transparent p-0 text-sm text-[var(--proxy-main)]">
            <span>{item.userId}</span>
            <RowArrow />
          </button>
        ),
      },
      {className: 'w-14 px-3', text: item.userCount},
      {
        className: 'w-[6rem] justify-center',
        text: toPriceStr(+item.rechargeAmount || 0),
      },
      {
        className: 'w-[6rem] justify-center',
        text: toPriceStr(+item.betAmount || 0),
      },
      {
        className: 'w-[6rem] justify-center',
        text: toPriceStr(+item.commissionAmount || 0),
      },
    ],
    [],
  );

  return (
    <div
      ref={scrollRef}
      onScroll={() => onScroll(scrollRef.current?.scrollLeft || 0)}
      className="flex flex-none flex-col items-start overflow-x-auto bg-white">
      {data.length === 0 ? (
        <div className="mb-6 flex min-h-[9.5rem] w-full flex-col items-center justify-center text-sm text-[var(--proxy-text)]">
          <img src={emptyImage} alt="" className="h-40 w-40 object-cover" />
          <span>No Data</span>
        </div>
      ) : (
        data.map((row, rowIndex) => (
          <div
            key={row.userId}
            className="flex h-10 flex-row gap-x-2 text-sm text-[var(--proxy-second)] even:bg-white odd:bg-[#F1F5F8]">
            {bodyCell(row).map((cell, cellIndex) => (
              <div
                key={cellIndex}
                className={`${
                  cellIndex === 0
                    ? `sticky left-0 border-r border-white ${
                        rowIndex % 2 === 1 ? 'bg-white' : 'bg-[#F1F5F8]'
                      }`
                    : ''
                } flex flex-none items-center ${cell.className || ''}`}>
                {cell.text}
              </div>
            ))}
          </div>
        ))
      )}
      {loading && (
        <div className="flex h-[6.25rem] w-full items-center justify-center text-sm text-[var(--proxy-text)]">
          Loading...
        </div>
      )}
    </div>
  );
});

const ProxyTeamReport = () => {
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const {t} = useTranslation();
  const tableHeaderScrollRef = useRef<TeamTableScrollRef>(null);
  const tableBodyScrollRef = useRef<TeamTableScrollRef>(null);
  const [tier, setTier] = useState('1');
  const [tableSort, setTableSort] = useState<Record<string, SortType>>({});
  const [tableDateRange, setTableDateRange] = useState<[number, number]>();

  const request = useCallback(
    (pageNo: number, pageSize: number) =>
      getTeamReportDataList({
        pageNo,
        pageSize,
        ...sortToApi(tableSort),
        searchWord: searching ? searchValue : '',
        ...(searching
          ? {}
          : {
              leve: +tier,
              startDate: tableDateRange?.[0]
                ? formatApiDate(tableDateRange[0])
                : undefined,
              endDate: tableDateRange?.[1]
                ? formatApiDate(tableDateRange[1])
                : undefined,
            }),
      } as any).then((result: any) => result),
    [searchValue, searching, tableDateRange, tableSort, tier],
  );

  const {items, totalSize, loading, loadingMore, isEnd, sentinelRef} =
    useInfinitePager<TeamReportListContent>(request);

  return (
    <div className="proxy-h5-page flex min-h-full flex-col bg-[#f3f5f8]">
      <ProxyH5Header title={t('proxy.team.title')} />
      <div className="flex flex-1 flex-col overflow-y-auto bg-[#f3f5f8]">
        <div className="flex flex-col gap-y-3">
          <div className="bg-white p-3">
            <SearchInputH5
              value={searchValue}
              onChange={setSearchValue}
              onSearch={() => setSearching(true)}
              onClearSearch={() => setSearching(false)}
            />
          </div>
          {searching ? (
            <div className="px-3 pb-3 text-sm text-[var(--proxy-main)]">
              Found{' '}
              <span className="text-[var(--proxy-primary)]">{totalSize}</span>{' '}
              results
            </div>
          ) : (
            <TeamReportPanelH5 />
          )}
        </div>
        <MyTeamsH5
          ref={tableHeaderScrollRef}
          tier={tier}
          searching={searching}
          dateRange={tableDateRange}
          onChangeTier={setTier}
          onChangeSort={setTableSort}
          onScroll={left => tableBodyScrollRef.current?.scrollTo(left)}
          onChangeDateRange={(value, startDate, endDate) => {
            setTableDateRange(
              startDate && endDate ? [startDate, endDate] : undefined,
            );
          }}
        />
        <TeamTableBodyH5
          ref={tableBodyScrollRef}
          loading={loading}
          data={items}
          onScroll={left => tableHeaderScrollRef.current?.scrollTo(left)}
        />
        <div ref={sentinelRef} />
        {loadingMore && (
          <div className="bg-white px-3 py-3 text-center text-xs text-[var(--proxy-text)]">
            Loading...
          </div>
        )}
        {!loading && !loadingMore && isEnd && items.length > 0 && (
          <div className="bg-white px-3 py-3 text-center text-xs text-[var(--proxy-text)]">
            No Data
          </div>
        )}
      </div>
    </div>
  );
};

export default ProxyTeamReport;
